// VIBRANIAM-SPACE v2 - DAGCanvas.jsx
// Pan/zoom canvas. Cables are dragged from an output dot to an input dot or
// straight onto a parameter row, which is how a modulation slot is created.
// Clicking a modulation cable opens its amount / curve / polarity editor.

import React, { useCallback, useMemo, useRef, useState } from 'react';
import ModuleNode from './ModuleNode.jsx';
import { NODE_TYPES, findPort } from '../engine/nodes/index.js';
import { kindColor, CURVES } from '../engine/types.js';
import { NODE_WIDTH, portOffset, nodeHeight } from './node-layout.js';

function edgePath(a, b) {
  const dx = Math.max(36, Math.abs(b.x - a.x) * 0.5);
  return `M ${a.x} ${a.y} C ${a.x + dx} ${a.y}, ${b.x - dx} ${b.y}, ${b.x} ${b.y}`;
}

export default function DAGCanvas({ graph, api, liveParams, onDropModule }) {
  const rawNodes = graph.nodes || [];
  const rawEdges = graph.edges || [];
  const nodes = rawNodes.filter(n => n !== null && n !== undefined && n.id !== undefined && n.id !== null);
  const edges = rawEdges.filter(e => e !== null && e !== undefined && e.from?.node && e.to?.node);
  const wrapRef = useRef(null);
  const [view, setView] = useState({ x: 20, y: 20, k: 0.8 });
  const [collapsed, setCollapsed] = useState(() => new Set());
  const [linking, setLinking] = useState(null);
  const dragRef = useRef(null);
  const panRef = useRef(null);

  const isCollapsed = useCallback((id) => collapsed.has(id), [collapsed]);

  const toCanvas = useCallback((clientX, clientY) => {
    const rect = wrapRef.current.getBoundingClientRect();
    return { x: (clientX - rect.left - view.x) / view.k, y: (clientY - rect.top - view.y) / view.k };
  }, [view]);

  const nodeById = useMemo(() => new Map(nodes.map((n) => [n.id, n])), [nodes]);

  const portPoint = useCallback((nodeId, portId, dir) => {
    const node = nodeById.get(nodeId);
    if (!node) return null;
    const off = portOffset(node, portId, dir, isCollapsed(nodeId));
    if (!off) return null;
    return { x: node.x + off.x, y: node.y + off.y };
  }, [nodeById, isCollapsed]);

  const handleNodeDragStart = useCallback((e, nodeId) => {
    if (e.button !== 0) return;
    const node = nodeById.get(nodeId);
    if (!node) return;
    const p = toCanvas(e.clientX, e.clientY);
    dragRef.current = { nodeId, dx: p.x - node.x, dy: p.y - node.y };
    // Same reason as the background pan: hold the pointer on the canvas for the
    // whole drag so leaving its bounds does not abort the gesture. Captured on
    // the wrapper (not the node) because the wrapper carries the move/up
    // handlers, and events keep bubbling to it either way. Cable dragging is
    // deliberately NOT captured - it has to land its pointerup on a port.
    try { wrapRef.current?.setPointerCapture(e.pointerId); } catch { /* unsupported */ }
  }, [nodeById, toCanvas]);

  const handleStartLink = useCallback((e, nodeId, portId) => {
    const p = toCanvas(e.clientX, e.clientY);
    setLinking({ from: { node: nodeId, port: portId }, cursor: p });
  }, [toCanvas]);

  const handleFinishLink = useCallback((e, nodeId, portId) => {
    if (!linking) return;
    api.connect(linking.from, { node: nodeId, port: portId });
    setLinking(null);
  }, [linking, api]);

  const handlePointerMove = useCallback((e) => {
    if (dragRef.current) {
      const p = toCanvas(e.clientX, e.clientY);
      const { nodeId, dx, dy } = dragRef.current;
      // Coalesce moveNode calls via rAF so a fast drag renders at most once
      // per frame instead of once per pointermove event.
      dragRef.current.pending = { nodeId, x: Math.round(p.x - dx), y: Math.round(p.y - dy) };
      if (!dragRef.current.rafPending) {
        dragRef.current.rafPending = true;
        requestAnimationFrame(() => {
          const pending = dragRef.current?.pending;
          if (pending && dragRef.current) {
            api.moveNode(pending.nodeId, pending.x, pending.y);
          }
          if (dragRef.current) dragRef.current.rafPending = false;
        });
      }
      return;
    }
    if (panRef.current) {
      // Read the pan origin HERE, not inside the updater. React runs a setState
      // updater later, during render - by then a pointerup or pointerleave may
      // already have set panRef.current back to null, and dereferencing it in
      // there is what produced "Cannot read properties of null (reading 'vx')".
      // The `if` above cannot protect code that runs after it returns; capturing
      // into locals is what actually closes the window.
      const { vx, vy, sx, sy } = panRef.current;
      setView((v) => ({ ...v, x: vx + (e.clientX - sx), y: vy + (e.clientY - sy) }));
      return;
    }
    if (linking) {
      const p = toCanvas(e.clientX, e.clientY);
      setLinking((l) => (l ? { ...l, cursor: p } : l));
    }
  }, [api, linking, toCanvas]);

  // Bound to pointerup, pointerleave and pointercancel on the wrapper. Leave
  // stays wired as the safety net for gestures that are NOT pointer-captured
  // (cable drags); captured gestures - node drag and background pan - no longer
  // fire it mid-drag, which is the point of capturing them.
  const handlePointerUp = useCallback((e) => {
    // Capture is released automatically on pointerup, but pointerleave and
    // pointercancel come through here too, so release explicitly.
    const pid = e?.pointerId;
    if (pid != null && wrapRef.current?.hasPointerCapture?.(pid)) {
      try { wrapRef.current.releasePointerCapture(pid); } catch { /* already gone */ }
    }
    dragRef.current = null;
    panRef.current = null;
    setLinking(null);
  }, []);

  const handleBackgroundDown = useCallback((e) => {
    if (e.target !== e.currentTarget) return;
    api.setSelectedId(null);
    api.setSelectedEdgeId(null);
    panRef.current = { sx: e.clientX, sy: e.clientY, vx: view.x, vy: view.y };
    // Own the pointer for the whole gesture. Without this a quick drag that
    // crosses the canvas edge fires pointerleave, which lands in
    // handlePointerUp and silently kills the pan half way through - and it was
    // also the most frequent way to open the null window described above.
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* unsupported */ }
  }, [api, view]);

  const handleWheel = useCallback((e) => {
    e.preventDefault();
    const rect = wrapRef.current.getBoundingClientRect();
    const mx = e.clientX - rect.left;
    const my = e.clientY - rect.top;
    setView((v) => {
      const k = Math.max(0.25, Math.min(2.2, v.k * (e.deltaY < 0 ? 1.1 : 1 / 1.1)));
      const s = k / v.k;
      return { k, x: mx - (mx - v.x) * s, y: my - (my - v.y) * s };
    });
  }, []);

  const toggleCollapse = useCallback((id) => {
    setCollapsed((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);

  const fitView = useCallback(() => {
    if (!nodes.length) return;
    const minX = Math.min(...nodes.map((n) => n.x)) - 40;
    const minY = Math.min(...nodes.map((n) => n.y)) - 40;
    const maxX = Math.max(...nodes.map((n) => n.x + NODE_WIDTH)) + 40;
    const maxY = Math.max(...nodes.map((n) => n.y + nodeHeight(n, isCollapsed(n.id)))) + 40;
    const rect = wrapRef.current.getBoundingClientRect();
    const k = Math.max(0.25, Math.min(1.2, Math.min(rect.width / (maxX - minX), rect.height / (maxY - minY))));
    setView({ k, x: -minX * k + 10, y: -minY * k + 10 });
  }, [nodes, isCollapsed]);

  const selectedEdge = (api && edges.find((e) => e.id === api.selectedEdgeId)) || null;
  const selectedEdgeMid = useMemo(() => {
    if (!selectedEdge) return null;
    const a = portPoint(selectedEdge.from.node, selectedEdge.from.port, 'out');
    const b = portPoint(selectedEdge.to.node, selectedEdge.to.port, 'in');
    if (!a || !b) return null;
    return { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
  }, [selectedEdge, portPoint]);

  return (
    <div
      ref={wrapRef}
      className="dag-grid relative h-full w-full overflow-hidden"
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerLeave={handlePointerUp}
      onPointerCancel={handlePointerUp}
      onPointerDown={handleBackgroundDown}
      onWheel={handleWheel}
      onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'copy'; }}
      onDrop={(e) => {
        e.preventDefault();
        const type = e.dataTransfer.getData('application/x-vs-module');
        if (!type || !NODE_TYPES[type]) return;
        const p = toCanvas(e.clientX, e.clientY);
        onDropModule(type, Math.round(p.x - NODE_WIDTH / 2), Math.round(p.y - 16));
      }}
    >
      <div
        className="absolute left-0 top-0 origin-top-left"
        style={{ transform: `translate(${view.x}px, ${view.y}px) scale(${view.k})` }}
      >
        <svg className="pointer-events-none absolute left-0 top-0 overflow-visible" width="1" height="1">
          {edges.map((e) => {
            const a = portPoint(e.from.node, e.from.port, 'out');
            const b = portPoint(e.to.node, e.to.port, 'in');
            if (!a || !b) return null;
            const fromNode = nodeById.get(e.from.node);
            const outPort = fromNode ? findPort(fromNode, e.from.port, 'out') : null;
            const isMod = e.to.port.startsWith('p:');
            const color = isMod ? '#4fd8ff' : kindColor(outPort?.kind);
            const sel = (api && api.selectedEdgeId === e.id);
            const strength = isMod ? Math.min(1, Math.abs(e.amount ?? 0.25) * 2.2) : 1;
            return (
              <g key={e.id} className="pointer-events-auto">
                <path
                  d={edgePath(a, b)} fill="none" stroke={color}
                  strokeOpacity={sel ? 1 : isMod ? 0.35 + strength * 0.45 : 0.75}
                  strokeWidth={sel ? 2.6 : isMod ? 1 + strength * 1.2 : 1.6}
                  strokeDasharray={isMod ? '5 3' : undefined}
                />
                <path
                  d={edgePath(a, b)} fill="none" stroke="transparent" strokeWidth="14"
                  style={{ cursor: 'pointer' }}
                  onPointerDown={(ev) => ev.stopPropagation()}
                  onClick={() => (isMod ? api.setSelectedEdgeId(e.id) : api.disconnect(e.id))}
                >
                  <title>{isMod ? 'Click per regolare la modulazione' : 'Click per scollegare'}</title>
                </path>
              </g>
            );
          })}
          {linking && (() => {
            const a = portPoint(linking.from.node, linking.from.port, 'out');
            if (!a) return null;
            return (
              <path d={edgePath(a, linking.cursor)} fill="none" stroke="#ffffff"
                strokeOpacity="0.6" strokeDasharray="4 4" strokeWidth="1.6" />
            );
          })()}
        </svg>

        {nodes.map((n) => (
          <ModuleNode
            key={n.id}
            node={n}
            edges={edges}
            selected={api.selectedId === n.id}
            collapsed={isCollapsed(n.id)}
            liveParams={liveParams ? liveParams[n.id] : null}
            onToggleCollapse={toggleCollapse}
            onSelect={api.setSelectedId}
            onDragStart={handleNodeDragStart}
            onRemove={api.removeNode}
            onDuplicate={api.duplicateNode}
            onParamChange={api.setParam}
            onStartLink={handleStartLink}
            onFinishLink={handleFinishLink}
          />
        ))}

        {selectedEdge && selectedEdgeMid && (
          <div
            className="node-shadow absolute w-56 rounded-md border border-vibe-cyan/60 bg-ink-700/97 p-2.5 backdrop-blur"
            style={{ left: selectedEdgeMid.x - 112, top: selectedEdgeMid.y + 14 }}
            onPointerDown={(e) => e.stopPropagation()}
          >
            <div className="mb-2 flex items-center justify-between">
              <span className="text-[10px] font-semibold uppercase tracking-wider text-vibe-cyan">
                Slot di modulazione
              </span>
              <button type="button" onClick={() => api.disconnect(selectedEdge.id)}
                className="rounded px-1 text-[11px] text-slate-400 hover:bg-red-500/20 hover:text-red-300">×</button>
            </div>
            <div className="mb-2 truncate font-mono text-[9px] text-slate-500">
              {nodeById.get(selectedEdge.from.node)?.type} → {selectedEdge.to.port.slice(2)}
            </div>
            <label className="mb-2 block">
              <div className="flex justify-between text-[9px] uppercase tracking-wider text-slate-500">
                <span>Ampiezza</span>
                <span className="font-mono text-slate-300">
                  {((selectedEdge.amount ?? 0.25) * 100).toFixed(0)}%
                </span>
              </div>
              <input type="range" min={-1} max={1} step={0.005}
                value={selectedEdge.amount ?? 0.25}
                onChange={(e) => api.setEdgeConfig(selectedEdge.id, { amount: Number(e.target.value) })}
                className="w-full" />
            </label>
            <div className="flex items-center gap-1.5">
              <select
                value={selectedEdge.curve || 'lin'}
                onChange={(e) => api.setEdgeConfig(selectedEdge.id, { curve: e.target.value })}
                className="flex-1 rounded bg-ink-600 px-1 py-0.5 text-[10px] text-slate-200 outline-none"
              >
                {CURVES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
              <button
                type="button"
                onClick={() => api.setEdgeConfig(selectedEdge.id, {
                  polarity: selectedEdge.polarity === 'neg' ? 'pos' : 'neg',
                })}
                className={`rounded px-2 py-0.5 text-[10px] ${
                  selectedEdge.polarity === 'neg'
                    ? 'bg-vibe-amber text-ink-900' : 'bg-ink-600 text-slate-300'
                }`}
              >
                {selectedEdge.polarity === 'neg' ? '−' : '+'}
              </button>
            </div>
          </div>
        )}
      </div>

      <div className="absolute bottom-3 left-3 flex items-center gap-1.5 rounded-md border border-white/10 bg-ink-800/90 px-2 py-1 text-[10px] text-slate-400 backdrop-blur">
        <button type="button" className="rounded px-1.5 py-0.5 hover:bg-white/10" onClick={fitView}>fit</button>
        <button type="button" className="rounded px-1.5 py-0.5 hover:bg-white/10"
          onClick={() => setView({ x: 20, y: 20, k: 0.8 })}>reset</button>
        <span className="font-mono">{Math.round(view.k * 100)}%</span>
        <span className="opacity-60">
          · {nodes.length} nodi · {edges.length} cavi · {api.modCount} modulazioni
        </span>
      </div>

      {api.lastError && (
        <div className="absolute right-3 top-3 rounded-md border border-amber-500/40 bg-amber-950/80 px-2.5 py-1.5 text-[10px] text-amber-200 backdrop-blur">
          {api.lastError}
        </div>
      )}
    </div>
  );
}
